import { useState, type ReactNode } from "react";
import { DashboardSection } from "@/components/dashboard-section";
import { TabNav } from "@/components/tab-nav";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Pencil } from "@/components/ui/icons";
import { PageHeader } from "@/components/page-header";
import { CoolIcon } from "@/components/ui/cool-icon";
import { useSession } from "@/lib/auth-client";

const TABS = [
  { key: "account", label: "My Account" },
  { key: "notifications", label: "Notifications" },
  { key: "kyc", label: "KYC" },
];

export function SettingsPage(): ReactNode {
  const [tab, setTab] = useState("account");

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Settings"
        subtitle="Account, Notifications & Identity Verification"
        icon={<CoolIcon name="Settings_Future" />}
        accentWords={["Settings"]}
      />
      <TabNav tabs={TABS} active={tab} onChange={setTab} />

      {tab === "account" && <AccountTab />}
      {tab === "notifications" && <NotificationsTab />}
      {tab === "kyc" && <KycTab />}
    </div>
  );
}

function AccountTab(): ReactNode {
  const [editing, setEditing] = useState(false);
  const { data: session } = useSession();
  const user = session?.user;
  // Better Auth stores one `name` field — we display it verbatim as
  // Display Name, and best-effort split on the first whitespace for the
  // legacy First/Last fields so the form structure stays intact.
  const fullName = user?.name?.trim() ?? "";
  const [firstName, ...rest] = fullName.length > 0 ? fullName.split(/\s+/) : [""];
  const lastName = rest.join(" ");
  const email = user?.email ?? "";

  return (
    <DashboardSection className="max-w-[538px]">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="text-xl font-light text-foreground">Personal Information</h2>
        <button
          onClick={() => setEditing(!editing)}
          className="flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-foreground transition-colors hover:bg-muted"
        >
          <Pencil className="h-3.5 w-3.5" />
          {editing ? "Cancel" : "Edit"}
        </button>
      </div>

      <div className="space-y-5">
        {/* Avatar placeholder */}
        <div className="mb-6 flex flex-col items-center">
          <div className="flex h-24 w-24 items-center justify-center rounded-full border-2 border-dashed border-border text-xs text-muted-foreground">
            Upload
          </div>
        </div>

        <FormField label="Display Name" value={fullName} disabled={!editing} />
        <FormField label="First Name" value={firstName ?? ""} disabled={!editing} />
        <FormField label="Last Name" value={lastName} disabled={!editing} />
        <FormField label="Email" value={email} type="email" disabled={!editing} />

        <div className="space-y-1.5">
          <label className="text-sm text-muted-foreground">Contact Number</label>
          <div className="flex gap-3">
            <input
              type="text"
              defaultValue="+90"
              disabled={!editing}
              className="w-1/4 rounded-lg bg-muted/40 px-4 py-3.5 text-sm text-foreground outline-none placeholder:text-muted-foreground disabled:opacity-60"
            />
            <input
              type="tel"
              defaultValue=""
              placeholder="Phone Number"
              disabled={!editing}
              className="flex-1 rounded-lg bg-muted/40 px-4 py-3.5 text-sm text-foreground outline-none placeholder:text-muted-foreground disabled:opacity-60"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <label className="text-sm text-muted-foreground">Timezone</label>
          <select
            disabled={!editing}
            defaultValue="Europe/Istanbul"
            className="w-full rounded-lg bg-muted/40 px-4 py-3.5 text-sm text-foreground outline-none disabled:opacity-60"
          >
            <option value="Europe/Istanbul">Europe/Istanbul (UTC+3)</option>
            <option value="UTC">UTC</option>
            <option value="America/New_York">America/New_York (UTC-5)</option>
            <option value="Europe/London">Europe/London (UTC+0)</option>
          </select>
        </div>

        {editing && (
          <button className="mt-4 w-full rounded-lg bg-accent px-4 py-3 text-sm font-medium text-white transition-colors hover:bg-accent/90">
            Save Changes
          </button>
        )}
      </div>
    </DashboardSection>
  );
}

function NotificationsTab(): ReactNode {
  return (
    <DashboardSection className="max-w-[538px]">
      <h2 className="mb-6 text-xl font-light text-foreground">Notification Preferences</h2>
      <div className="space-y-5">
        <NotifToggle label="Drawdown Alerts" description="Get notified when drawdown approaches limits" defaultChecked />
        <NotifToggle label="Trade Executions" description="Notifications for trade open/close events" defaultChecked />
        <NotifToggle label="Payout Updates" description="Status updates on payout requests" defaultChecked />
        <NotifToggle label="News Blackouts" description="Alerts before high-impact news events" />
        <NotifToggle label="Rule Changes" description="Notifications when firm rules are updated" />
        <NotifToggle label="Discount Alerts" description="New discounts on prop firm challenges" />
      </div>
    </DashboardSection>
  );
}

function KycTab(): ReactNode {
  return (
    <DashboardSection className="max-w-[538px]">
      <h2 className="mb-6 text-xl font-light text-foreground">Identity Verification</h2>
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <p className="mb-4 text-sm text-muted-foreground">
          KYC verification is required for funded accounts and payouts.
        </p>
        <button className="rounded-lg bg-accent px-6 py-2.5 text-sm font-medium text-white transition-colors hover:bg-accent/90">
          Start Verification
        </button>
      </div>
    </DashboardSection>
  );
}

// ── Helpers ──

function FormField({
  label,
  value,
  type = "text",
  disabled,
}: {
  label: string;
  value: string;
  type?: string;
  disabled: boolean;
}): ReactNode {
  return (
    <div className="space-y-1.5">
      <label className="text-sm text-muted-foreground">{label}</label>
      <input
        // key forces a remount when `value` changes — useSession() resolves
        // async, so the initial render has an empty `value` that must be
        // replaced once the session fetch completes. Without this key the
        // `defaultValue` below only reads on first mount and the input
        // stays empty forever.
        key={value}
        type={type}
        defaultValue={value}
        disabled={disabled}
        className="w-full rounded-lg bg-muted/40 px-4 py-3.5 text-sm text-foreground outline-none placeholder:text-muted-foreground disabled:opacity-60"
      />
    </div>
  );
}

function NotifToggle({
  label,
  description,
  defaultChecked,
}: {
  label: string;
  description: string;
  defaultChecked?: boolean;
}): ReactNode {
  return (
    <div className="flex items-center justify-between gap-4 border-b border-border/20 pb-4 last:border-0 last:pb-0">
      <div>
        <Label className="text-sm font-medium text-foreground">{label}</Label>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      <Switch defaultChecked={defaultChecked} />
    </div>
  );
}
